import React, { useState, useCallback } from 'react';
import { Lead } from '../types';
import { leadsService } from '../services/api';

interface KanbanBoardProps {
  leads: Lead[];
  onStatusUpdate: (leadId: number, newStatus: string) => void;
  onEditLead: (lead: Lead) => void;
  onWhatsAppOpen: (lead: Lead) => void;
  loading: boolean;
}

const KanbanBoard: React.FC<KanbanBoardProps> = ({ 
  leads, 
  onStatusUpdate, 
  onEditLead, 
  onWhatsAppOpen, 
  loading 
}) => {
  const [draggedLead, setDraggedLead] = useState<Lead | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);

  const columns = [
    { 
      id: 'novo', 
      title: '🆕 Novos', 
      color: 'bg-blue-100 border-blue-300',
      headerColor: 'bg-blue-200'
    },
    { 
      id: 'interessado', 
      title: '👀 Interessados', 
      color: 'bg-yellow-100 border-yellow-300',
      headerColor: 'bg-yellow-200'
    },
    { 
      id: 'contato_inicial', 
      title: '📞 Contato Inicial', 
      color: 'bg-purple-100 border-purple-300',
      headerColor: 'bg-purple-200'
    },
    { 
      id: 'em_negociacao', 
      title: '💬 Em Negociação', 
      color: 'bg-orange-100 border-orange-300',
      headerColor: 'bg-orange-200'
    },
    { 
      id: 'proposta_enviada', 
      title: '📋 Proposta Enviada', 
      color: 'bg-indigo-100 border-indigo-300',
      headerColor: 'bg-indigo-200'
    },
    { 
      id: 'fechado_ganho', 
      title: '✅ Fechado Ganho', 
      color: 'bg-green-100 border-green-300',
      headerColor: 'bg-green-200'
    },
    { 
      id: 'fechado_perdido', 
      title: '❌ Fechado Perdido', 
      color: 'bg-red-100 border-red-300',
      headerColor: 'bg-red-200'
    }
  ];

  const getLeadsByStatus = (status: string) => {
    return leads.filter(lead => (lead.status_lead || 'novo') === status);
  };

  const formatPhone = (phone: string | null) => {
    if (!phone) return '-';
    const cleanPhone = phone.replace(/\D/g, '');
    if (cleanPhone.length === 11) {
      return cleanPhone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
    return phone;
  };

  const handleDragStart = (e: React.DragEvent, lead: Lead) => {
    setDraggedLead(lead);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(columnId);
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  const handleDrop = async (e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    setDragOverColumn(null);
    
    if (draggedLead && draggedLead.status_lead !== columnId) {
      try {
        await onStatusUpdate(draggedLead.id, columnId);
      } catch (error) {
        console.error('Erro ao atualizar status:', error);
      }
    }
    setDraggedLead(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        <span className="ml-3 text-gray-600">Carregando Kanban...</span>
      </div>
    );
  }

  return (
    <div className="w-full overflow-x-auto pb-4">
      <div className="flex space-x-4 min-w-max">
        {columns.map((column) => {
          const columnLeads = getLeadsByStatus(column.id);
          const isDragOver = dragOverColumn === column.id;
          
          return (
            <div
              key={column.id}
              className={`flex-shrink-0 w-80 ${column.color} rounded-lg border-2 transition-all duration-200 ${
                isDragOver ? 'border-dashed border-4 scale-105' : ''
              }`}
              onDragOver={(e) => handleDragOver(e, column.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, column.id)}
            >
              {/* Header da Coluna */}
              <div className={`${column.headerColor} p-4 rounded-t-lg border-b-2 border-gray-300`}>
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-800">{column.title}</h3>
                  <span className="bg-white px-2 py-1 rounded-full text-sm font-medium text-gray-700">
                    {columnLeads.length}
                  </span>
                </div>
              </div>

              {/* Cards dos Leads */}
              <div className="p-3 space-y-3 min-h-[200px] max-h-[calc(100vh-300px)] overflow-y-auto">
                {columnLeads.map((lead) => (
                  <div
                    key={lead.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, lead)}
                    className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 cursor-move hover:shadow-md transition-all duration-200 hover:scale-[1.02]"
                  >
                    {/* Cabeçalho do Card */}
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        #{lead.numero_processo?.slice(-6) || lead.id}
                      </span>
                      <div className="flex space-x-1">
                        <button
                          onClick={() => onEditLead(lead)}
                          className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                          title="Editar Lead"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => onWhatsAppOpen(lead)}
                          className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                          title="WhatsApp"
                        >
                          📱
                        </button>
                      </div>
                    </div>

                    {/* Informações principais */}
                    <div className="space-y-2">
                      <div>
                        <p className="font-medium text-gray-900 text-sm leading-tight">
                          {lead.reu_nome || 'Nome não informado'}
                        </p>
                        <p className="text-xs text-gray-500">{lead.reu_cpf}</p>
                      </div>
                      
                      <div>
                        <p className="text-sm text-gray-700">
                          <span className="font-medium">Autor:</span> {lead.autor_nome}
                        </p>
                      </div>

                      <div className="flex items-center justify-between">
                        <p className="text-sm text-gray-600">
                          📞 {formatPhone(lead.reu_telefone_1)}
                        </p>
                        {lead.comprou && (
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                            💰 Comprou
                          </span>
                        )}
                      </div>

                      {lead.valor_causa && (
                        <div className="pt-2 border-t border-gray-100">
                          <p className="text-xs text-gray-600">
                            <span className="font-medium">Valor:</span> R$ {Number(lead.valor_causa).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </p>
                        </div>
                      )}

                      {lead.consultor && (
                        <div className="flex items-center text-xs text-gray-500">
                          👤 {lead.consultor_nome || `Consultor ${lead.consultor}`}
                        </div>
                      )}

                      {lead.tags && lead.tags.length > 0 && (
                        <div className="pt-2 border-t border-gray-100">
                          <div className="flex flex-wrap gap-1">
                            {lead.tags.slice(0, 2).map(tag => (
                              <span
                                key={tag.id}
                                className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium"
                                style={{ 
                                  backgroundColor: `${tag.color}20`, 
                                  color: tag.color,
                                  border: `1px solid ${tag.color}40`
                                }}
                              >
                                {tag.name}
                              </span>
                            ))}
                            {lead.tags.length > 2 && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                                +{lead.tags.length - 2}
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {lead.observacoes && (
                        <div className="pt-2 border-t border-gray-100">
                          <p className="text-xs text-gray-600">
                            <span className="font-medium">📝 Obs:</span> 
                            <span className="block mt-1 text-gray-500 line-clamp-2">
                              {lead.observacoes.length > 80 
                                ? `${lead.observacoes.substring(0, 80)}...`
                                : lead.observacoes
                              }
                            </span>
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {columnLeads.length === 0 && (
                  <div className="text-center py-8 text-gray-400">
                    <div className="text-3xl mb-2">📋</div>
                    <p className="text-sm">Nenhum lead nesta fase</p>
                    <p className="text-xs mt-1">Arraste leads para cá</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default KanbanBoard;